import { captureException } from '@sentry/react-router';
import { HoneypotProvider } from '@veraclins-dev/form';
import { createHoneypot } from '@veraclins-dev/form/server';
import { EpicToaster, useToast } from '@veraclins-dev/react-utils';
import { combineHeaders, getToast } from '@veraclins-dev/react-utils/server';
import { Box, IconProvider, Typography } from '@veraclins-dev/ui';
import href from '@veraclins-dev/ui/sprite.svg';
import { cn } from '@veraclins-dev/utils';
import { useEffect } from 'react';
import {
  data,
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useMatches,
} from 'react-router';

import { ClientHintCheck, getHints, type Theme, useTheme } from '@veraclins/ui';
import { getTheme } from '@veraclins/ui/server';

import { type Route } from './+types/root';
import { Footer, type FooterProps } from './components/footer';
import { Header } from './components/header';
import { defaultSocialImage, siteBaseUrl } from './content/metadata';
import { site } from './content/site';
import { AppLink } from './link';
import styles from './styles.css?url';

type FooterHandle =
  | FooterProps
  | ((params: Record<string, string | undefined>) => FooterProps | undefined);

export const meta: Route.MetaFunction = () => {
  return [
    { title: site.title },

    { name: 'description', content: site.description },
    { name: 'author', content: site.name },
    {
      name: 'theme-color',
      content: '#f4efe6',
      media: '(prefers-color-scheme: light)',
    },
    {
      name: 'theme-color',
      content: '#211f1a',
      media: '(prefers-color-scheme: dark)',
    },
    // Open Graph
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: site.currentDomain },
    { property: 'og:title', content: site.title },
    { property: 'og:description', content: site.description },
    { property: 'og:url', content: siteBaseUrl },
    { property: 'og:image', content: defaultSocialImage },
    // Twitter Card
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: site.title },
    { name: 'twitter:description', content: site.description },
    { name: 'twitter:image', content: defaultSocialImage },
    { name: 'twitter:creator', content: '@icstps' },
    // Additional SEO
    { name: 'robots', content: 'index, follow' },
    { name: 'googlebot', content: 'index, follow' },
    { tagName: 'link', rel: 'canonical', href: siteBaseUrl },
  ];
};

export const links: Route.LinksFunction = () => [
  { rel: 'stylesheet', href: styles },
  { rel: 'preload', href, as: 'image' },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Geist:wght@100..900&family=JetBrains+Mono:wght@400;500;600;700&family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;0,6..72,600;0,6..72,700;1,6..72,400;1,6..72,500&display=swap',
  },
];

export async function loader({ request }: Route.LoaderArgs) {
  const honeyProps = process.env.HONEYPOT_SEED
    ? await createHoneypot().getInputProps()
    : null;
  const { toast, headers: toastHeaders } = await getToast(request);

  return data(
    {
      theme: getTheme(request),
      honeyProps,
      toast,
      requestInfo: {
        hints: getHints(request),
        userPrefs: { theme: getTheme(request) },
      },
      features: {
        packagesSubdomain: process.env.ENABLE_PACKAGES_SUBDOMAIN === 'true',
      },
    },
    {
      headers: combineHeaders(toastHeaders),
    },
  );
}

function Document({
  children,
  theme = 'light',
}: {
  children: React.ReactNode;
  theme?: Theme | null;
}) {
  const resolvedTheme = theme ?? 'light';
  const matches = useMatches();
  const footerMatch = [...matches]
    .reverse()
    .find(
      (match) =>
        (match.handle as { footer?: FooterHandle } | undefined)?.footer,
    );
  const footerHandle = (
    footerMatch?.handle as { footer?: FooterHandle } | undefined
  )?.footer;
  const footerProps =
    typeof footerHandle === 'function'
      ? footerHandle(footerMatch?.params ?? {})
      : footerHandle;
  return (
    <html
      lang="en"
      className={cn(resolvedTheme, 'h-screen w-screen')}
      suppressHydrationWarning
    >
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        <Meta />
        <Links />
        <ClientHintCheck />
        {/* Structured Data - JSON-LD */}
        <script
          type="application/ld+json"
          // skipcq: JS-0440
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Person',
              name: site.name,
              jobTitle: 'Founder and CEO, Veraclins Tech Solutions',
              url: siteBaseUrl,
              sameAs: [
                site.social.github,
                site.social.linkedin,
                site.social.twitter,
              ],
              telephone: site.contact.phone,
              address: {
                '@type': 'PostalAddress',
                addressLocality: 'Abuja',
                addressCountry: 'NG',
              },
              description: site.description,
            }),
          }}
        />
      </head>
      <body className="bg-background h-full text-base text-foreground antialiased">
        {/* Skip to main content link for accessibility */}
        {/* oxlint-disable-next-line veraclins-ui/no-bare-html -- Link injects base variant classes (inline-flex, rings, transitions) that would alter this sr-only skip link's exact rendered output */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg"
        >
          Skip to main content
        </a>
        <IconProvider sprite={href}>
          <Box
            mx="auto"
            display="flex"
            minH="full"
            w="full"
            flexDirection="column"
            px={{ xs: 5, sm: 8, lg: 12 }}
            // oxlint-disable-next-line veraclins-ui/prefer-style-props -- max-w-310 arbitrary px width has no maxW scale-token/prop equivalent
            className="max-w-310"
          >
            <Header />
            <Box component="main" id="main-content" tabIndex={-1}>
              {children}
            </Box>
            <Footer {...footerProps} />
          </Box>
        </IconProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function ErrorBoundary({ error, loaderData }: Route.ErrorBoundaryProps) {
  const theme = loaderData?.theme || null;

  // Capture non-route errors to Sentry
  useEffect(() => {
    if (!isRouteErrorResponse(error) && process.env.NODE_ENV === 'production') {
      captureException(error);
    }
  }, [error]);

  return (
    <Document theme={theme}>
      <Box
        component="section"
        flex="1"
        className="container"
        display="flex"
        flexDirection="column"
        items="center"
        justify="center"
        gap={6}
        paddingY={16}
        paddingX={4}
      >
        {isRouteErrorResponse(error) ? (
          <>
            <Typography
              variant="small"
              className="font-mono uppercase tracking-widest text-primary"
            >
              {error.status}
            </Typography>
            {error.statusText && (
              <Typography
                variant="small"
                className="font-mono uppercase tracking-widest text-muted-foreground"
              >
                {error.statusText}
              </Typography>
            )}
            <Typography variant="h1" maxW="3xl" className="text-center">
              This route did not return the page it should have.
            </Typography>
            <Typography
              variant="body1"
              maxW="2xl"
              className="text-center text-muted-foreground"
            >
              {typeof error.data === 'string'
                ? error.data
                : 'The useful public pages are still available from the index.'}
            </Typography>
          </>
        ) : error instanceof Error ? (
          <>
            <Typography
              variant="small"
              className="font-mono uppercase tracking-widest text-primary"
            >
              Error
            </Typography>
            <Typography variant="h1" maxW="3xl" className="text-center">
              Something went wrong
            </Typography>
            <Typography
              variant="body1"
              maxW="2xl"
              className="text-center text-muted-foreground"
            >
              A load-bearing piece of this page failed. Refreshing may clear it;
              otherwise the index and contact routes are still the fastest way
              through.
            </Typography>
            {error.message && (
              <Box
                maxW="2xl"
                rounded="md"
                border={1}
                p={4}
                // oxlint-disable-next-line veraclins-ui/prefer-style-props -- border-danger/20 is a border-color utility with no style-prop equivalent
                className="border-danger/20 bg-danger/10"
              >
                <Typography className="text-danger font-mono text-xs">
                  {error.message}
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <>
            <Typography
              variant="small"
              className="font-mono uppercase tracking-widest text-primary"
            >
              Error
            </Typography>
            <Typography variant="h1" maxW="3xl" className="text-center">
              Something went wrong
            </Typography>
          </>
        )}
        <Box display="flex" gap={4} flexWrap="wrap" justify="center">
          <AppLink
            to="/"
            type="button"
            emphasis="filled"
            tone="brand"
            linkSize="lg"
          >
            Go to index
          </AppLink>
          <AppLink
            to="/contact"
            type="button"
            emphasis="outline"
            tone="brand"
            linkSize="lg"
          >
            Contact
          </AppLink>
        </Box>
      </Box>
    </Document>
  );
}

export default function App({ loaderData }: Route.ComponentProps) {
  const theme = useTheme();

  useToast(loaderData.toast);

  const app = (
    <Document theme={theme}>
      <Outlet />
      <EpicToaster position="bottom-right" theme={theme} richColors />
    </Document>
  );

  return loaderData.honeyProps ? (
    <HoneypotProvider {...loaderData.honeyProps}>{app}</HoneypotProvider>
  ) : (
    app
  );
}
